// LineupButton — drop-in "Add to Lineup" / "Queued" toggle that
// appears next to creator-post cards, channel-video tiles, and
// anywhere else a creator video shows up. Stays in sync with the
// global lineup count so multiple buttons on the same page reflect
// each other's state.
import React, { useEffect, useState } from "react";
import { Queue, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { addToLineup, removeFromLineup } from "../lib/lineup";

// We broadcast lineup changes through a CustomEvent so every button
// on the page can react instantly without a re-fetch round-trip.
const EVENT = "hw:lineup-changed";

export function emitLineupChange(postId, queued, count, title) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { postId, queued, count, title } }));
}

export default function LineupButton({ postId, initialQueued = false, size = "sm", className = "" }) {
  const [queued, setQueued] = useState(initialQueued);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onChange = (e) => {
      if (e.detail?.postId === postId) setQueued(!!e.detail.queued);
    };
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, [postId]);

  const click = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    // optimistic flip
    const target = !queued;
    setQueued(target);
    try {
      const fn = target ? addToLineup : removeFromLineup;
      const res = await fn(postId);
      emitLineupChange(postId, !!res?.queued, res?.count);
      toast.success(target ? "Added to lineup" : "Removed from lineup", { duration: 1800 });
    } catch (err) {
      setQueued(!target);
      const msg = err?.response?.data?.detail || "Couldn't update your lineup";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const sz = size === "md" ? "h-10 px-4 text-xs" : "h-8 px-3 text-[11px]";
  return (
    <button
      onClick={click}
      disabled={busy}
      data-testid={`lineup-toggle-${postId}`}
      aria-pressed={queued ? "true" : "false"}
      aria-label={queued ? "Remove from lineup" : "Add to lineup"}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold tracking-wide transition-all ${sz} ${
        queued
          ? "bg-forest text-cream hover:bg-forest-600"
          : "bg-cream-200 text-forest hover:bg-cream-300 border border-forest/10"
      } ${className}`}
    >
      {queued ? <Check size={13} weight="bold" /> : <Queue size={13} weight="bold" />}
      {queued ? "In lineup" : "Lineup"}
    </button>
  );
}
